import * as RadixAccordion from "@radix-ui/react-accordion";
import * as RadixTabs from "@radix-ui/react-tabs";
import { ChevronDown } from "lucide-react";
import type { ReactNode } from "react";
import { NavLink } from "react-router";
import { Icon } from "../icons/Icon";
import { cx, FOCUS_RING } from "../styles";

/**
 * Tabs, in two shapes and two mechanisms.
 *
 * The mechanism is the decision worth making, and it is not cosmetic.
 *
 * **Radix tabs** hold their state in the component. Right for a panel where the
 * choice is transient and nobody would want to link to it — a chart's units, a
 * preview's width.
 *
 * **`asLinks` renders `NavLink`s**, and the URL is the state. Right for
 * anything somebody might bookmark, send to a colleague, or reach with the back
 * button — a record's sections, a settings area. `/stores/s1/users` is a place;
 * a tab index in React state is not, and a detail screen whose tab resets on
 * every reload is a screen people stop deep-linking into.
 *
 * The two are one component because the *appearance* is identical and screens
 * should not have to reimplement an underline to get routing.
 */

export type TabsVariant = "underline" | "segmented";

const LIST: Record<TabsVariant, string> = {
	underline: "flex items-center gap-1 border-b border-border",
	segmented: "inline-flex gap-0.5 rounded-sm border border-border bg-bg-subtle p-0.5",
};

/**
 * A tab's look, split into the part that is always true and the two states.
 *
 * Split rather than written as `data-[state=active]:` variants, because only
 * half the tabs in this file have a `data-state` to key off: `LinkTabs` renders
 * `NavLink`s, which report their own active state through a render prop. One
 * class string with Radix's attribute selectors in it would have to be
 * string-manipulated for the link version — which an earlier draft did, and
 * which breaks silently the first time a variant gains a class nobody thought
 * about.
 */
const TAB: Record<TabsVariant, { base: string; active: string; inactive: string }> = {
	// 38px with a 2px brand underline, from the design. The underline is on the
	// tab itself rather than a sliding indicator: one element, no animation to
	// get wrong, and it stays put under reduced motion.
	underline: {
		base: "h-[38px] cursor-pointer border-b-2 px-3 text-[13px] max-sm:min-h-12",
		active: "border-brand font-bold text-fg",
		inactive: "border-transparent font-medium text-fg-muted hover:text-fg",
	},
	segmented: {
		base: "h-[26px] cursor-pointer rounded-[5px] px-3 text-[12.5px] max-sm:min-h-12",
		active: "bg-bg-surface font-bold text-fg shadow-xs",
		inactive: "font-medium text-fg-muted hover:text-fg",
	},
};

/** The classes for a tab in one state or the other. */
function tabClasses(variant: TabsVariant, active: boolean): string {
	const tab = TAB[variant];
	return cx(
		"inline-flex shrink-0 items-center gap-2 whitespace-nowrap no-underline",
		tab.base,
		active ? tab.active : tab.inactive,
		FOCUS_RING,
	);
}

export interface TabDefinition {
	value: string;
	label: ReactNode;
	/** A count or a status beside the label. */
	badge?: ReactNode;
	disabled?: boolean;
}

export interface TabsProps {
	tabs: TabDefinition[];
	value?: string;
	defaultValue?: string;
	onValueChange?: (value: string) => void;
	variant?: TabsVariant;
	/** Names the strip. Without it a screen reader announces a list of tabs with no subject. */
	"aria-label"?: string;
	children?: ReactNode;
	className?: string;
}

export function Tabs({
	tabs,
	variant = "underline",
	children,
	className,
	"aria-label": label,
	...rest
}: TabsProps) {
	return (
		<RadixTabs.Root className={cx("flex flex-col gap-4", className)} {...rest}>
			<RadixTabs.List aria-label={label} className={cx(LIST[variant], "overflow-x-auto")}>
				{tabs.map((tab) => (
					<RadixTabs.Trigger
						key={tab.value}
						value={tab.value}
						disabled={tab.disabled}
						// Radix owns the active state here, so the classes are resolved
						// from its `data-state` rather than from a boolean this component
						// holds.
						className={cx(
							tabClasses(variant, false),
							variant === "underline"
								? "data-[state=active]:border-brand data-[state=active]:font-bold data-[state=active]:text-fg"
								: "data-[state=active]:bg-bg-surface data-[state=active]:font-bold data-[state=active]:text-fg data-[state=active]:shadow-xs",
							"disabled:cursor-not-allowed disabled:text-fg-subtle",
						)}
					>
						{tab.label}
						{tab.badge}
					</RadixTabs.Trigger>
				))}
			</RadixTabs.List>
			{children}
		</RadixTabs.Root>
	);
}

/** One tab's panel. */
export const TabPanel = RadixTabs.Content;

export interface LinkTab {
	/** The route. `end` matches exactly, for a parent path that would otherwise stay active. */
	to: string;
	label: ReactNode;
	badge?: ReactNode;
	end?: boolean;
}

export interface LinkTabsProps {
	tabs: LinkTab[];
	variant?: TabsVariant;
	"aria-label"?: string;
	className?: string;
}

/**
 * A tab strip that is really navigation.
 *
 * `role="tablist"` is deliberately *not* used here, and that is the point of
 * having two components rather than a prop. These are links: a screen reader
 * should announce them as navigation, the browser should let somebody
 * middle-click one into a new window, and `aria-current="page"` is what says
 * which is active. Dressing links up as tabs would take all of that away in
 * exchange for looking the same, which it already does.
 */
export function LinkTabs({ tabs, variant = "underline", className, ...rest }: LinkTabsProps) {
	return (
		<nav className={cx(LIST[variant], "overflow-x-auto", className)} {...rest}>
			{tabs.map((tab) => (
				<NavLink
					key={tab.to}
					to={tab.to}
					{...(tab.end === undefined ? {} : { end: tab.end })}
					// NavLink reports its own active state, and sets `aria-current` —
					// which is what actually tells a screen reader which one you are on.
					className={({ isActive }) => tabClasses(variant, isActive)}
				>
					{tab.label}
					{tab.badge}
				</NavLink>
			))}
		</nav>
	);
}

export interface AccordionSection {
	value: string;
	title: ReactNode;
	/** A count or status beside the title. */
	meta?: ReactNode;
	content: ReactNode;
}

export interface AccordionProps {
	sections: AccordionSection[];
	/** `single` closes the others; `multiple` lets several stand open. */
	type?: "single" | "multiple";
	defaultValue?: string | string[];
	className?: string;
}

/**
 * Collapsible sections.
 *
 * `multiple` by default, because the thing this is for — the dashboard's
 * notification groups, a long settings page — is somebody comparing two
 * sections, and an accordion that shuts one to open another makes that
 * impossible.
 */
export function Accordion({
	sections,
	type = "multiple",
	defaultValue,
	className,
}: AccordionProps) {
	const shared = {
		className: cx(
			"flex flex-col divide-y divide-border rounded-md border border-border",
			className,
		),
	};

	const items = sections.map((section) => (
		<RadixAccordion.Item key={section.value} value={section.value}>
			<RadixAccordion.Header>
				<RadixAccordion.Trigger
					className={cx(
						"flex w-full cursor-pointer items-center gap-2 px-4 py-3 text-start text-[13px] font-bold text-fg",
						"hover:bg-bg-subtle group max-sm:min-h-12",
						FOCUS_RING,
					)}
				>
					{/* Rotates rather than swapping glyphs, so there is one icon and no
					    chance of the two drifting apart. Down and up are the same in
					    both directions, so it does not mirror. */}
					<Icon
						as={ChevronDown}
						size={16}
						className="text-fg-subtle transition-transform group-data-[state=open]:rotate-180 motion-reduce:transition-none"
					/>
					<span className="flex-1">{section.title}</span>
					{section.meta}
				</RadixAccordion.Trigger>
			</RadixAccordion.Header>
			<RadixAccordion.Content className="px-4 pb-3 text-[13px] text-fg-muted">
				{section.content}
			</RadixAccordion.Content>
		</RadixAccordion.Item>
	));

	return type === "single" ? (
		<RadixAccordion.Root
			type="single"
			collapsible
			{...(typeof defaultValue === "string" ? { defaultValue } : {})}
			{...shared}
		>
			{items}
		</RadixAccordion.Root>
	) : (
		<RadixAccordion.Root
			type="multiple"
			{...(Array.isArray(defaultValue) ? { defaultValue } : {})}
			{...shared}
		>
			{items}
		</RadixAccordion.Root>
	);
}
